import {
  ArrowLeftOutlined,
  CameraOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Button, Card, Form, Input, Switch, Upload } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { BRAND } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import {
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useRemoveCustomerImageMutation,
  useUpdateCustomerMutation,
  useUploadCustomerImageMutation,
} from '@/modules/customers/customersApi';
import type { Customer } from '@/types/customer';
import { resolveMutationResult } from '@/utils/apiResponse';
import { resolveCustomerImageUrl } from '@/utils/media';
import styles from './CustomerForm.module.scss';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

interface CustomerFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  pincode: string;
  country: string;
  state: string;
  city: string;
  address: string;
  landmark: string;
  isActive: boolean;
}

export default function CustomerForm() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerQuery(id ?? '', {
    skip: !isEdit || !id,
  });
  const [createCustomer, createState] = useCreateCustomerMutation();
  const [updateCustomer, updateState] = useUpdateCustomerMutation();
  const [uploadCustomerImage, uploadState] = useUploadCustomerImageMutation();
  const [removeCustomerImage, removeImageState] = useRemoveCustomerImageMutation();

  const [imagePreview, setImagePreview] = useState<string | undefined>();
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  const isSaving =
    createState.isLoading ||
    updateState.isLoading ||
    uploadState.isLoading ||
    removeImageState.isLoading;

  useGlobalLoader(isSaving);
  useGlobalLoader(isEdit && isLoadingCustomer);

  const { control, handleSubmit, reset } = useForm<CustomerFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      pincode: '',
      country: 'India',
      state: '',
      city: '',
      address: '',
      landmark: '',
      isActive: true,
    },
  });

  useEffect(() => {
    if (!isEdit || !customer) return;

    reset({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      password: '',
      pincode: customer.address?.pincode ?? '',
      country: customer.address?.country ?? 'India',
      state: customer.address?.state ?? '',
      city: customer.address?.city ?? '',
      address: customer.address?.address ?? '',
      landmark: customer.address?.landmark ?? '',
      isActive: customer.isActive,
    });
    setImagePreview(resolveCustomerImageUrl(customer));
    setPendingImageFile(null);
  }, [customer, isEdit, reset]);

  const avatarSrc = useMemo(
    () => imagePreview ?? (customer ? resolveCustomerImageUrl(customer) : undefined),
    [customer, imagePreview],
  );

  const handleImageSelect = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return false;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return false;
    }

    setPendingImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    return false;
  };

  const handleRemoveImage = async () => {
    setPendingImageFile(null);
    setImagePreview(undefined);

    if (!isEdit || !customer?.profileImage) return;

    const result = await resolveMutationResult(removeCustomerImage(customer.id));
    if (result.ok) {
      notify.success(result.message ?? 'Profile image removed.');
      return;
    }

    notify.error(result.message);
    setImagePreview(resolveCustomerImageUrl(customer));
  };

  const onSubmit = async (values: CustomerFormValues) => {
    const address = {
      pincode: values.pincode.trim(),
      country: values.country.trim(),
      state: values.state.trim(),
      city: values.city.trim(),
      address: values.address.trim(),
      landmark: values.landmark.trim() || undefined,
    };

    if (isEdit) {
      if (!id) return;
      const result = await resolveMutationResult<Customer>(
        updateCustomer({
          id,
          body: {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            address,
            isActive: values.isActive,
          },
        }),
      );
      if (!result.ok) {
        notify.error(result.message);
        return;
      }

      const customerId = id;
      if (pendingImageFile) {
        const formData = new FormData();
        formData.append('image', pendingImageFile);
        const uploadResult = await resolveMutationResult(
          uploadCustomerImage({ id: customerId, formData }),
        );

        if (!uploadResult.ok) {
          notify.error(uploadResult.message);
          navigate(`/customers/${customerId}`);
          return;
        }
      }

      notify.success(result.message ?? 'Customer updated successfully.');
      navigate(`/customers/${customerId}`);
      return;
    }

    if (!values.password?.trim()) {
      notify.error('Password is required for new customers.');
      return;
    }

    const result = await resolveMutationResult<Customer>(
      createCustomer({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        address,
        isActive: values.isActive,
      }),
    );
    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    const customerId = result.data.id;
    if (pendingImageFile) {
      const formData = new FormData();
      formData.append('image', pendingImageFile);
      const uploadResult = await resolveMutationResult(
        uploadCustomerImage({ id: customerId, formData }),
      );

      if (!uploadResult.ok) {
        notify.error(uploadResult.message);
        navigate(ROUTES.CUSTOMERS);
        return;
      }
    }

    notify.success(result.message ?? 'Customer created successfully.');
    navigate(ROUTES.CUSTOMERS);
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(isEdit ? `/customers/${id}` : ROUTES.CUSTOMERS)}
            >
              Back
            </Button>
            <Button type="primary" loading={isSaving} onClick={handleSubmit(onSubmit)}>
              {isEdit ? 'Save customer' : 'Create customer'}
            </Button>
          </>
        }
      />

      <Card loading={isEdit && isLoadingCustomer} className={styles.card}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <div className={styles.imagePicker}>
            <Upload
              accept={ACCEPTED_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={handleImageSelect}
              disabled={isSaving}
            >
              <button type="button" className={styles.imageThumb} disabled={isSaving}>
                {avatarSrc ? (
                  <PreviewImage
                    src={avatarSrc}
                    alt="Customer profile"
                    fill
                    shape="circle"
                    className={styles.imagePreview}
                    stopPropagation
                  />
                ) : (
                  <span className={styles.imagePlaceholder}>
                    <PictureOutlined />
                  </span>
                )}
                <span className={styles.imageOverlay}>
                  <CameraOutlined />
                </span>
              </button>
            </Upload>

            <div className={styles.imageMeta}>
              <p className={styles.imageTitle}>
                {avatarSrc ? 'Profile photo selected' : 'Upload profile photo'}
              </p>
              <p className={styles.imageHint}>Optional · PNG, JPG, WEBP or GIF · Max 5MB</p>
              {avatarSrc ? (
                <div className={styles.imageActions}>
                  <Upload
                    accept={ACCEPTED_TYPES.join(',')}
                    showUploadList={false}
                    beforeUpload={handleImageSelect}
                    disabled={isSaving}
                  >
                    <Button size="small" type="default">
                      Change
                    </Button>
                  </Upload>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => void handleRemoveImage()}
                    disabled={isSaving}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          <div className={styles.gridTwo}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="First name"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="First name" />
                </Form.Item>
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Last name"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="Last name" />
                </Form.Item>
              )}
            />
          </div>

          <div className={styles.gridTwo}>
            <Controller
              name="email"
              control={control}
              rules={{ required: 'Email is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Email"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} type="email" placeholder="customer@example.com" />
                </Form.Item>
              )}
            />

            {!isEdit ? (
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Password"
                    required
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message ?? '8–20 chars with upper, lower, number, special'}
                  >
                    <Input {...field} type="password" placeholder="Reader@12345" />
                  </Form.Item>
                )}
              />
            ) : (
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Status">
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      style={{ background: field.value ? BRAND.primary : undefined }}
                    />
                  </Form.Item>
                )}
              />
            )}
          </div>

          <div className={styles.sectionTitle}>Delivery address</div>

          <div className={styles.gridFour}>
            <Controller
              name="pincode"
              control={control}
              rules={{ required: 'Pincode is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Pincode"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="110020" />
                </Form.Item>
              )}
            />

            <Controller
              name="country"
              control={control}
              rules={{ required: 'Country is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Country"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="India" />
                </Form.Item>
              )}
            />

            <Controller
              name="state"
              control={control}
              rules={{ required: 'State is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="State"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="Delhi" />
                </Form.Item>
              )}
            />

            <Controller
              name="city"
              control={control}
              rules={{ required: 'City is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="City"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="New Delhi" />
                </Form.Item>
              )}
            />
          </div>

          <div className={styles.gridTwo}>
            <Controller
              name="address"
              control={control}
              rules={{ required: 'Address is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Address"
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input.TextArea {...field} rows={2} placeholder="Street address" />
                </Form.Item>
              )}
            />

            <Controller
              name="landmark"
              control={control}
              render={({ field }) => (
                <Form.Item label="Landmark (optional)">
                  <Input {...field} placeholder="Near metro station" />
                </Form.Item>
              )}
            />
          </div>

          {!isEdit ? (
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Form.Item label="Status">
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    style={{ background: field.value ? BRAND.primary : undefined }}
                  />
                </Form.Item>
              )}
            />
          ) : null}
        </Form>
      </Card>
    </div>
  );
}
